import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownAZ,
  ArrowUpAZ,
  Copy,
  ExternalLink,
  FileSpreadsheet,
  Link2,
  Link2Off,
  Loader2,
  Plus,
  Redo2,
  RotateCcw,
  Save,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import {
  coerceAssistantWorkbookArtifact,
} from './assistantWorkbookArtifact';
import { TailwindDateField } from '../forms/TailwindDateField';
import { SupportErrorMessage } from '../overlays/EngineeringSupportBanner';
import type {
  AdmissionsAssistantArtifactWorkspaceRef,
  AdmissionsAssistantWorkbookArtifact,
  AdmissionsAssistantWorkbookCellFormat,
  AdmissionsAssistantSpreadsheetViewContext,
} from './types';
import {
  addSpreadsheetWorkbookColumn,
  addSpreadsheetWorkbookRow,
  addSpreadsheetWorkbookSheet,
  coerceSpreadsheetCellValue,
  deleteSpreadsheetWorkbookColumn,
  deleteSpreadsheetWorkbookRow,
  deleteSpreadsheetWorkbookSheet,
  duplicateSpreadsheetWorkbookRow,
  evaluateSpreadsheetFormulaValue,
  getSpreadsheetWorkbookCellFormat,
  reorderSpreadsheetWorkbookColumns,
  reorderSpreadsheetWorkbookSheets,
  setSpreadsheetWorkbookCellFormat,
  updateSpreadsheetWorkbookCell,
  updateSpreadsheetWorkbookColumn,
  updateSpreadsheetWorkbookSheet,
  type SpreadsheetWorkbookCellEditableValue,
} from '../../shared/assistant-spreadsheet-operations';
import {
  applySpreadsheetSheetViewFilters,
  createSpreadsheetSheetFilterState,
  deriveSpreadsheetSheetFilterDefinitions,
  type SpreadsheetActiveFilterChip,
  type SpreadsheetGeneratedFilterDefinition,
  type SpreadsheetSheetFilterState,
  type SpreadsheetVisibleRow,
} from '../../shared/assistant-spreadsheet-filters';
import {
  useGetAssistantSpreadsheetWorkbookDraftStateQuery,
  useRestoreAssistantSpreadsheetWorkbookDraftMutation,
  useSaveAssistantSpreadsheetWorkbookDraftMutation,
} from '../../state/domainRouterApi';
import { AppDialogFrame } from '../overlays/AppDialogFrame';

type AssistantSpreadsheetWorkspaceProps = {
  workbookArtifact: AdmissionsAssistantArtifactWorkspaceRef;
  spreadsheetDownloadArtifact?: AdmissionsAssistantArtifactWorkspaceRef | null;
  bridgeConnected: boolean;
  bridgeStatusLabel: string;
  bridgeState: 'connected' | 'not_connected' | 'download_only' | 'needs_attention';
  bridgeSupportedEnvironment: boolean;
  bridgePresets: Array<Record<string, unknown>>;
  exportState: 'idle' | 'submitting' | 'success' | 'error';
  exportMessage?: string | null;
  onExport?: (input: {
    fileName: string;
    destinationFileId?: string;
    destinationFolderId?: string;
  }) => void;
  onClose: () => void;
  onStartBridgeAuth?: (() => void | Promise<void>) | null;
  onDisconnectBridge?: (() => void | Promise<void>) | null;
  onSavePreset?: ((input: {
    presetId?: string;
    label: string;
    destinationType: 'new_sheet' | 'existing_sheet';
    destinationFileId?: string;
    destinationFolderId?: string;
    spreadsheetUrl?: string | null;
    fileNameTemplate?: string | null;
  }) => void | Promise<void>) | null;
  onDeletePreset?: ((input: { presetId: string }) => void | Promise<void>) | null;
  onSessionChange?: ((input: {
    workbookArtifact: AdmissionsAssistantArtifactWorkspaceRef;
    spreadsheetDownloadArtifact?: AdmissionsAssistantArtifactWorkspaceRef | null;
    view?: AdmissionsAssistantSpreadsheetViewContext | null;
  }) => void) | null;
  presentation?: 'portal' | 'embedded';
};

type SpreadsheetDraftVersion = {
  versionId: string;
  versionNumber: number;
  savedAt: string;
  updatedBy: string;
  title: string;
  fileName: string;
  isCurrent: boolean;
};

type SpreadsheetPreset = {
  presetId: string;
  label: string;
  destinationType: 'new_sheet' | 'existing_sheet';
  destinationFileId?: string;
  destinationFolderId?: string;
  spreadsheetUrl?: string | null;
  fileNameTemplate?: string | null;
};

type SelectedCell = {
  rowIndex: number;
  columnKey: string;
};

type FilterSearchState = Record<string, string>;

type InlineCommitInputProps = {
  value: SpreadsheetWorkbookCellEditableValue;
  onCommit: (value: SpreadsheetWorkbookCellEditableValue) => void;
  onFocus?: () => void;
};

const asRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
);

const toText = (value: unknown): string => (
  typeof value === 'string' ? value.trim() : ''
);

const toArtifactRef = (value: unknown): AdmissionsAssistantArtifactWorkspaceRef | null => {
  const record = asRecord(value);
  if (!record || typeof record.downloadUrl !== 'string') {
    return null;
  }
  return {
    id: typeof record.id === 'string' ? record.id : `artifact-${Math.random().toString(36).slice(2)}`,
    artifactType: typeof record.artifactType === 'string' ? record.artifactType : 'spreadsheet-workbook',
    label: typeof record.label === 'string' ? record.label : 'Spreadsheet artifact',
    fileName: typeof record.fileName === 'string' ? record.fileName : 'assistant-workbook.json',
    downloadUrl: record.downloadUrl,
    mimeType: typeof record.mimeType === 'string' ? record.mimeType : null,
    sourceRef: typeof record.sourceRef === 'string' ? record.sourceRef : null,
    expiresAtLabel: typeof record.expiresAtLabel === 'string' ? record.expiresAtLabel : undefined,
  };
};

const parseDraftVersions = (value: unknown): SpreadsheetDraftVersion[] => (
  Array.isArray(value)
    ? value
      .map((entry) => {
        const record = asRecord(entry);
        if (!record) {
          return null;
        }
        const versionId = toText(record.versionId);
        if (!versionId) {
          return null;
        }
        return {
          versionId,
          versionNumber: typeof record.versionNumber === 'number' ? record.versionNumber : 0,
          savedAt: toText(record.savedAt),
          updatedBy: toText(record.updatedBy) || 'assistant',
          title: toText(record.title) || 'Spreadsheet draft',
          fileName: toText(record.fileName) || 'assistant-export.xlsx',
          isCurrent: record.isCurrent === true,
        };
      })
      .filter((entry): entry is SpreadsheetDraftVersion => Boolean(entry))
    : []
);

const parsePresets = (value: Array<Record<string, unknown>>): SpreadsheetPreset[] => (
  value
    .flatMap((record) => {
      const presetId = toText(record.presetId);
      if (!presetId) {
        return [];
      }
      return [{
        presetId,
        label: toText(record.label) || 'Untitled preset',
        destinationType: record.destinationType === 'existing_sheet' ? 'existing_sheet' : 'new_sheet',
        destinationFileId: toText(record.destinationFileId) || undefined,
        destinationFolderId: toText(record.destinationFolderId) || undefined,
        spreadsheetUrl: toText(record.spreadsheetUrl) || null,
        fileNameTemplate: toText(record.fileNameTemplate) || null,
      }];
    })
);

const extractSpreadsheetId = (value: string): string => {
  const normalized = value.trim();
  const match = normalized.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/i);
  return match?.[1] ?? normalized;
};

const sheetColumnWidthClass = (width: number | undefined): string => {
  if (!width || width <= 14) return 'min-w-[10rem]';
  if (width <= 22) return 'min-w-[13rem]';
  if (width <= 30) return 'min-w-[16rem]';
  return 'min-w-[20rem]';
};

const InlineCommitInput: React.FC<InlineCommitInputProps> = ({ value, onCommit, onFocus }) => {
  const [draft, setDraft] = useState(() => value == null ? '' : String(value));

  useEffect(() => {
    setDraft(value == null ? '' : String(value));
  }, [value]);

  return (
    <input
      value={draft}
      onFocus={onFocus}
      onChange={(event) => setDraft(event.currentTarget.value)}
      onBlur={() => onCommit(coerceSpreadsheetCellValue(draft))}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          (event.currentTarget as HTMLInputElement).blur();
        }
      }}
      className="w-full rounded-sm border border-transparent bg-transparent px-2 py-1 text-[13px] text-[#404F4D] outline-none focus:border-[#2697A2]/30 focus:bg-white"
    />
  );
};

const reportPreviewIconButtonClass =
  'inline-flex h-8 w-8 items-center justify-center rounded-sm text-[#9CA3AF] transition-colors hover:bg-[#F4F6F3] hover:text-[#2697A2] disabled:cursor-not-allowed disabled:opacity-45';
const reportPreviewSecondaryButtonClass =
  'inline-flex h-8 items-center gap-1.5 rounded-sm border border-[#E5E7EB] bg-white px-3 text-[12px] font-medium text-[#6B7280] transition-colors hover:bg-[#F8FAF9] hover:text-[#404F4D] disabled:cursor-not-allowed disabled:opacity-45';
const reportPreviewPrimaryButtonClass =
  'inline-flex h-8 items-center gap-1.5 rounded-sm bg-[#2697A2] px-3 text-[12px] font-semibold text-white transition-colors hover:bg-[#08BFAE] disabled:cursor-not-allowed disabled:opacity-45';
const reportPreviewInputClass =
  'h-8 min-w-[7.5rem] rounded-sm border border-[#E5E7EB] bg-white px-3 text-[12px] text-[#404F4D] outline-none transition-colors placeholder:text-[#9CA3AF] focus:border-[#2697A2]/40';
const reportPreviewSurfaceClass =
  'rounded-lg border border-[#E5E7EB] bg-white shadow-[0_0px_3px_2px_rgba(206,242,237,0.24)]';

export const AssistantSpreadsheetWorkspace: React.FC<AssistantSpreadsheetWorkspaceProps> = ({
  workbookArtifact,
  spreadsheetDownloadArtifact,
  bridgeConnected,
  bridgeStatusLabel,
  bridgeState,
  bridgeSupportedEnvironment,
  bridgePresets,
  exportState,
  exportMessage,
  onExport,
  onClose,
  onStartBridgeAuth,
  onDisconnectBridge,
  onSavePreset,
  onDeletePreset,
  onSessionChange,
  presentation = 'portal',
}) => {
  const [resolvedWorkbookArtifact, setResolvedWorkbookArtifact] = useState(workbookArtifact);
  const [resolvedDownloadArtifact, setResolvedDownloadArtifact] = useState(spreadsheetDownloadArtifact ?? null);
  const [artifact, setArtifact] = useState<AdmissionsAssistantWorkbookArtifact | null>(null);
  const [workingCopy, setWorkingCopy] = useState<AdmissionsAssistantWorkbookArtifact | null>(null);
  const [activeSheetId, setActiveSheetId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [historyPast, setHistoryPast] = useState<AdmissionsAssistantWorkbookArtifact[]>([]);
  const [historyFuture, setHistoryFuture] = useState<AdmissionsAssistantWorkbookArtifact[]>([]);
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null);
  const [newColumnHeader, setNewColumnHeader] = useState('');
  const [newSheetName, setNewSheetName] = useState('');
  const [sheetFilterStateById, setSheetFilterStateById] = useState<Record<string, SpreadsheetSheetFilterState>>({});
  const [filterSearchById, setFilterSearchById] = useState<FilterSearchState>({});
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [destinationMode, setDestinationMode] = useState<'new_sheet' | 'existing_sheet'>('new_sheet');
  const [destinationFileInput, setDestinationFileInput] = useState('');
  const [destinationFolderInput, setDestinationFolderInput] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [presetLabel, setPresetLabel] = useState('');
  const [draftFileName, setDraftFileName] = useState(
    (spreadsheetDownloadArtifact?.fileName || 'assistant-export.xlsx').replace(/\.json$/i, '.xlsx'),
  );

  const draftStateQuery = useGetAssistantSpreadsheetWorkbookDraftStateQuery(
    { workbookArtifactPath: workbookArtifact.sourceRef ?? '' },
    {
      skip: !workbookArtifact.sourceRef,
      // Reopening a workbook must re-resolve from durable sourceRef instead of
      // trusting a warm RTK cache entry that may carry stale transport URLs.
      refetchOnMountOrArgChange: true,
    },
  );
  const [saveDraftMutation] = useSaveAssistantSpreadsheetWorkbookDraftMutation();
  const [restoreDraftMutation] = useRestoreAssistantSpreadsheetWorkbookDraftMutation();

  const draftPayload = useMemo(
    () => asRecord((draftStateQuery.data as Record<string, unknown> | undefined)?.payload),
    [draftStateQuery.data],
  );
  const draftVersions = useMemo(
    () => parseDraftVersions(draftPayload?.versions),
    [draftPayload],
  );
  const presets = useMemo(
    () => parsePresets(bridgePresets),
    [bridgePresets],
  );

  useEffect(() => {
    const nextWorkbookArtifact = toArtifactRef(draftPayload?.workbookArtifact);
    const nextDownloadArtifact = toArtifactRef(draftPayload?.spreadsheetDownloadArtifact);
    if (nextWorkbookArtifact) {
      setResolvedWorkbookArtifact(nextWorkbookArtifact);
      setResolvedDownloadArtifact(nextDownloadArtifact ?? null);
      setDraftFileName((nextDownloadArtifact?.fileName || draftFileName).replace(/\.json$/i, '.xlsx'));
      onSessionChange?.({
        workbookArtifact: nextWorkbookArtifact,
        spreadsheetDownloadArtifact: nextDownloadArtifact ?? null,
      });
    }
  }, [draftFileName, draftPayload, onSessionChange]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setErrorMessage(null);

    fetch(resolvedWorkbookArtifact.downloadUrl)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Workbook artifact request failed (${response.status}).`);
        }
        return await response.json();
      })
      .then((payload) => {
        if (cancelled) {
          return;
        }
        const nextArtifact = coerceAssistantWorkbookArtifact(payload);
        if (!nextArtifact) {
          throw new Error('Workbook artifact payload is invalid.');
        }
        setArtifact(nextArtifact);
        setWorkingCopy(nextArtifact);
        setActiveSheetId(nextArtifact.sheets[0]?.id ?? null);
        setHistoryPast([]);
        setHistoryFuture([]);
        setSheetFilterStateById({});
        setFilterSearchById({});
        const workbookSourceRef = resolvedWorkbookArtifact.sourceRef ?? nextArtifact.metadata?.workbookArtifactPath ?? null;
        const downloadSourceRef = resolvedDownloadArtifact?.sourceRef ?? nextArtifact.metadata?.xlsxArtifactPath ?? null;
        if (workbookSourceRef && workbookSourceRef !== resolvedWorkbookArtifact.sourceRef) {
          const nextWorkbookArtifact = {
            ...resolvedWorkbookArtifact,
            sourceRef: workbookSourceRef,
          };
          const nextSpreadsheetDownloadArtifact = resolvedDownloadArtifact
            ? {
              ...resolvedDownloadArtifact,
              sourceRef: downloadSourceRef ?? resolvedDownloadArtifact.sourceRef ?? null,
            }
            : null;
          setResolvedWorkbookArtifact(nextWorkbookArtifact);
          if (nextSpreadsheetDownloadArtifact) {
            setResolvedDownloadArtifact(nextSpreadsheetDownloadArtifact);
          }
          onSessionChange?.({
            workbookArtifact: nextWorkbookArtifact,
            spreadsheetDownloadArtifact: nextSpreadsheetDownloadArtifact,
          });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setErrorMessage(error instanceof Error ? error.message : 'Unable to load spreadsheet workspace.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedWorkbookArtifact.downloadUrl]);

  const activeSheet = useMemo(
    () => workingCopy?.sheets.find((sheet) => sheet.id === activeSheetId) ?? workingCopy?.sheets[0] ?? null,
    [activeSheetId, workingCopy],
  );

  const generatedFilterDefinitions = useMemo<SpreadsheetGeneratedFilterDefinition[]>(
    () => activeSheet ? deriveSpreadsheetSheetFilterDefinitions(activeSheet) : [],
    [activeSheet],
  );

  const activeSheetFilterState = useMemo<SpreadsheetSheetFilterState>(
    () => (activeSheet ? sheetFilterStateById[activeSheet.id] ?? createSpreadsheetSheetFilterState() : createSpreadsheetSheetFilterState()),
    [activeSheet, sheetFilterStateById],
  );

  const activeSheetView = useMemo(
    () => activeSheet
      ? applySpreadsheetSheetViewFilters({
        sheet: activeSheet,
        definitions: generatedFilterDefinitions,
        state: activeSheetFilterState,
      })
      : {
        rows: [] as SpreadsheetVisibleRow[],
        totalRowCount: 0,
        visibleRowCount: 0,
        activeFilterChips: [] as SpreadsheetActiveFilterChip[],
      },
    [activeSheet, activeSheetFilterState, generatedFilterDefinitions],
  );

  const updateActiveSheetFilterState = useCallback((updater: (current: SpreadsheetSheetFilterState) => SpreadsheetSheetFilterState) => {
    if (!activeSheet) {
      return;
    }
    setSheetFilterStateById((current) => ({
      ...current,
      [activeSheet.id]: updater(current[activeSheet.id] ?? createSpreadsheetSheetFilterState()),
    }));
  }, [activeSheet]);

  const setFilterSearch = useCallback((columnKey: string, value: string) => {
    if (!activeSheet) {
      return;
    }
    const key = `${activeSheet.id}:${columnKey}`;
    setFilterSearchById((current) => ({
      ...current,
      [key]: value,
    }));
  }, [activeSheet]);

  const clearAllGeneratedFilters = useCallback(() => {
    if (!activeSheet) {
      return;
    }
    setSheetFilterStateById((current) => ({
      ...current,
      [activeSheet.id]: createSpreadsheetSheetFilterState(),
    }));
  }, [activeSheet]);

  const clearActiveFilterChip = useCallback((chip: SpreadsheetActiveFilterChip) => {
    updateActiveSheetFilterState((current) => {
      if (chip.clearAction.kind === 'categorical') {
        const value = chip.clearAction.value;
        return {
          ...current,
          categoricalSelections: {
            ...current.categoricalSelections,
            [chip.columnKey]: (current.categoricalSelections[chip.columnKey] ?? []).filter((entry) => entry !== value),
          },
        };
      }
      if (chip.clearAction.kind === 'boolean') {
        return {
          ...current,
          booleanSelections: {
            ...current.booleanSelections,
            [chip.columnKey]: 'all',
          },
        };
      }
      if (chip.clearAction.kind === 'date-bucket') {
        const value = chip.clearAction.value;
        const selection = current.dateSelections[chip.columnKey] ?? { buckets: [], start: null, end: null };
        return {
          ...current,
          dateSelections: {
            ...current.dateSelections,
            [chip.columnKey]: {
              ...selection,
              buckets: selection.buckets.filter((entry) => entry !== value),
            },
          },
        };
      }
      if (chip.clearAction.kind === 'date-start' || chip.clearAction.kind === 'date-end') {
        const selection = current.dateSelections[chip.columnKey] ?? { buckets: [], start: null, end: null };
        return {
          ...current,
          dateSelections: {
            ...current.dateSelections,
            [chip.columnKey]: {
              ...selection,
              start: chip.clearAction.kind === 'date-start' ? null : selection.start,
              end: chip.clearAction.kind === 'date-end' ? null : selection.end,
            },
          },
        };
      }
      if (chip.clearAction.kind === 'numeric-min' || chip.clearAction.kind === 'numeric-max') {
        const selection = current.numericSelections[chip.columnKey] ?? { min: '', max: '' };
        return {
          ...current,
          numericSelections: {
            ...current.numericSelections,
            [chip.columnKey]: {
              min: chip.clearAction.kind === 'numeric-min' ? '' : selection.min,
              max: chip.clearAction.kind === 'numeric-max' ? '' : selection.max,
            },
          },
        };
      }
      return current;
    });
  }, [updateActiveSheetFilterState]);

  const sessionView = useMemo<AdmissionsAssistantSpreadsheetViewContext | null>(() => {
    if (!activeSheet || !resolvedWorkbookArtifact.sourceRef) {
      return null;
    }
    return {
      workbookArtifactPath: resolvedWorkbookArtifact.sourceRef ?? null,
      workbookTitle: workingCopy?.title ?? artifact?.title ?? resolvedWorkbookArtifact.label,
      activeSheetId: activeSheet.id,
      activeSheetName: activeSheet.name,
      visibleRowCount: activeSheetView.visibleRowCount,
      totalRowCount: activeSheetView.totalRowCount,
      activeFilters: activeSheetView.activeFilterChips.map((chip) => ({
        columnKey: chip.columnKey,
        label: chip.label,
        valueLabel: chip.valueLabel,
      })),
    };
  }, [
    activeSheet,
    activeSheetView.activeFilterChips,
    activeSheetView.totalRowCount,
    activeSheetView.visibleRowCount,
    artifact?.title,
    resolvedWorkbookArtifact.label,
    resolvedWorkbookArtifact.sourceRef,
    workingCopy?.title,
  ]);

  useEffect(() => {
    onSessionChange?.({
      workbookArtifact: resolvedWorkbookArtifact,
      spreadsheetDownloadArtifact: resolvedDownloadArtifact ?? null,
      view: sessionView,
    });
  }, [onSessionChange, resolvedDownloadArtifact, resolvedWorkbookArtifact, sessionView]);

  const commitWorkbook = (nextWorkbook: AdmissionsAssistantWorkbookArtifact) => {
    if (workingCopy) {
      setHistoryPast((current) => [...current.slice(-39), workingCopy]);
      setHistoryFuture([]);
    }
    setWorkingCopy(nextWorkbook);
    setSaveState('idle');
    setSaveMessage('Draft updated locally. Save a new version when ready.');
  };

  const selectedCellValue = useMemo(() => {
    if (!activeSheet || !selectedCell) {
      return null;
    }
    const row = activeSheet.rows[selectedCell.rowIndex];
    return row?.[selectedCell.columnKey] ?? null;
  }, [activeSheet, selectedCell]);

  const selectedCellFormat = useMemo<AdmissionsAssistantWorkbookCellFormat | null>(() => {
    if (!activeSheet || !selectedCell) {
      return null;
    }
    return getSpreadsheetWorkbookCellFormat({
      sheet: activeSheet,
      rowIndex: selectedCell.rowIndex,
      columnKey: selectedCell.columnKey,
    });
  }, [activeSheet, selectedCell]);

  const selectedFormulaPreview = useMemo(() => {
    if (!activeSheet || !selectedCell || typeof selectedCellValue !== 'string' || !selectedCellValue.startsWith('=')) {
      return null;
    }
    const row = activeSheet.rows[selectedCell.rowIndex];
    return evaluateSpreadsheetFormulaValue({
      formula: selectedCellValue as `=${string}`,
      row,
    });
  }, [activeSheet, selectedCell, selectedCellValue]);

  const handleUndo = () => {
    const previous = historyPast[historyPast.length - 1];
    if (!previous || !workingCopy) {
      return;
    }
    setHistoryPast((current) => current.slice(0, -1));
    setHistoryFuture((current) => [workingCopy, ...current]);
    setWorkingCopy(previous);
  };

  const handleRedo = () => {
    const next = historyFuture[0];
    if (!next || !workingCopy) {
      return;
    }
    setHistoryFuture((current) => current.slice(1));
    setHistoryPast((current) => [...current.slice(-39), workingCopy]);
    setWorkingCopy(next);
  };

  const handleSaveDraft = async () => {
    if (!workingCopy || !resolvedWorkbookArtifact.sourceRef) {
      return;
    }
    setSaveState('saving');
    setSaveMessage('Saving spreadsheet draft...');
    try {
      const result = await saveDraftMutation({
        workbookArtifactPath: resolvedWorkbookArtifact.sourceRef,
        fileName: draftFileName.replace(/\.json$/i, '.xlsx'),
        workbook: workingCopy as unknown as Record<string, unknown>,
      }).unwrap();
      const payload = asRecord(result.payload);
      const nextWorkbookArtifact = toArtifactRef(payload?.workbookArtifact);
      const nextDownloadArtifact = toArtifactRef(payload?.spreadsheetDownloadArtifact);
      if (nextWorkbookArtifact) {
        setResolvedWorkbookArtifact(nextWorkbookArtifact);
        setResolvedDownloadArtifact(nextDownloadArtifact ?? null);
        onSessionChange?.({
          workbookArtifact: nextWorkbookArtifact,
          spreadsheetDownloadArtifact: nextDownloadArtifact ?? null,
        });
      }
      setArtifact(workingCopy);
      setSaveState('success');
      setSaveMessage('Spreadsheet draft saved as a new version.');
      void draftStateQuery.refetch();
    } catch (error) {
      setSaveState('error');
      setSaveMessage(error instanceof Error ? error.message : 'Unable to save spreadsheet draft.');
    }
  };

  const handleRestoreVersion = async (versionId: string) => {
    if (!resolvedWorkbookArtifact.sourceRef) {
      return;
    }
    setSaveState('saving');
    setSaveMessage('Restoring spreadsheet version...');
    try {
      const result = await restoreDraftMutation({
        workbookArtifactPath: resolvedWorkbookArtifact.sourceRef,
        versionId,
      }).unwrap();
      const payload = asRecord(result.payload);
      const nextWorkbookArtifact = toArtifactRef(payload?.workbookArtifact);
      const nextDownloadArtifact = toArtifactRef(payload?.spreadsheetDownloadArtifact);
      if (nextWorkbookArtifact) {
        setResolvedWorkbookArtifact(nextWorkbookArtifact);
        setResolvedDownloadArtifact(nextDownloadArtifact ?? null);
        onSessionChange?.({
          workbookArtifact: nextWorkbookArtifact,
          spreadsheetDownloadArtifact: nextDownloadArtifact ?? null,
        });
      }
      setSaveState('success');
      setSaveMessage('Spreadsheet version restored.');
      void draftStateQuery.refetch();
    } catch (error) {
      setSaveState('error');
      setSaveMessage(error instanceof Error ? error.message : 'Unable to restore spreadsheet version.');
    }
  };

  const getFilterSearchValue = (columnKey: string): string => (
    activeSheet ? filterSearchById[`${activeSheet.id}:${columnKey}`] ?? '' : ''
  );

  const renderGeneratedFilterSection = (definition: SpreadsheetGeneratedFilterDefinition) => {
    if (!activeSheet) {
      return null;
    }

    if (definition.kind === 'categorical') {
      const selectedValues = activeSheetFilterState.categoricalSelections[definition.columnKey] ?? [];
      const searchValue = getFilterSearchValue(definition.columnKey);
      const visibleOptions = searchValue.trim().length === 0
        ? definition.options
        : definition.options.filter((option) => (
          option.label.toLowerCase().includes(searchValue.trim().toLowerCase())
        ));
      return (
        <details
          key={definition.columnKey}
          className="rounded-[20px] border border-slate-200 bg-white p-3"
          open={definition.presentation === 'chips'}
          data-testid={`assistant-spreadsheet-filter-${definition.columnKey}`}
        >
          <summary className="cursor-pointer list-none">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">{definition.header}</p>
                <p className="text-[11px] text-irongrey/70">
                  {definition.options.length} option{definition.options.length === 1 ? '' : 's'}
                </p>
              </div>
              {selectedValues.length > 0 ? (
                <span className="rounded-full bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary">
                  {selectedValues.length}
                </span>
              ) : null}
            </div>
          </summary>
          <div className="mt-3 space-y-3">
            {definition.presentation === 'multi_select' ? (
              <input
                value={searchValue}
                onChange={(event) => setFilterSearch(definition.columnKey, event.currentTarget.value)}
                placeholder={`Search ${definition.header.toLowerCase()}`}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-slate-700"
              />
            ) : null}
            <div className="flex max-h-64 flex-wrap gap-2 overflow-y-auto pr-1">
              {visibleOptions.map((option) => {
                const isSelected = selectedValues.includes(option.value);
                const buttonClassName = definition.presentation === 'chips'
                  ? [
                    'rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors',
                    isSelected
                      ? 'border-primary/20 bg-primary text-white'
                      : 'border-slate-200 bg-slate-50 text-irongrey hover:bg-primary/5',
                  ].join(' ')
                  : [
                    'flex w-full items-center justify-between rounded-2xl border px-3 py-2 text-left text-[12px] transition-colors',
                    isSelected
                      ? 'border-primary/20 bg-primary/5 text-slate-900'
                      : 'border-slate-200 bg-white text-irongrey hover:bg-slate-50',
                  ].join(' ');
                return (
                  <button
                    key={`${definition.columnKey}:${option.value}`}
                    type="button"
                    className={buttonClassName}
                    onClick={() => {
                      updateActiveSheetFilterState((current) => {
                        const existing = current.categoricalSelections[definition.columnKey] ?? [];
                        const next = existing.includes(option.value)
                          ? existing.filter((value) => value !== option.value)
                          : [...existing, option.value];
                        return {
                          ...current,
                          categoricalSelections: {
                            ...current.categoricalSelections,
                            [definition.columnKey]: next,
                          },
                        };
                      });
                    }}
                  >
                    <span>{option.label}</span>
                    <span className="ml-3 text-[11px] text-irongrey/60">{option.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </details>
      );
    }

    if (definition.kind === 'boolean') {
      const selection = activeSheetFilterState.booleanSelections[definition.columnKey] ?? 'all';
      return (
        <details
          key={definition.columnKey}
          className="rounded-[20px] border border-slate-200 bg-white p-3"
          open
          data-testid={`assistant-spreadsheet-filter-${definition.columnKey}`}
        >
          <summary className="cursor-pointer list-none">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">{definition.header}</p>
                <p className="text-[11px] text-irongrey/70">Quick toggle</p>
              </div>
            </div>
          </summary>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {[
              { value: 'all' as const, label: 'All', count: activeSheet.rows.length },
              { value: 'true' as const, label: definition.trueLabel, count: definition.trueCount },
              { value: 'false' as const, label: definition.falseLabel, count: definition.falseCount },
            ].map((option) => (
              <button
                key={`${definition.columnKey}:${option.value}`}
                type="button"
                className={[
                  'rounded-2xl border px-3 py-2 text-[12px] font-semibold transition-colors',
                  selection === option.value
                    ? 'border-primary/20 bg-primary text-white'
                    : 'border-slate-200 bg-slate-50 text-irongrey hover:bg-primary/5',
                ].join(' ')}
                onClick={() => {
                  updateActiveSheetFilterState((current) => ({
                    ...current,
                    booleanSelections: {
                      ...current.booleanSelections,
                      [definition.columnKey]: option.value,
                    },
                  }));
                }}
              >
                <span>{option.label}</span>
                <span className="ml-1 text-[11px] opacity-80">({option.count})</span>
              </button>
            ))}
          </div>
        </details>
      );
    }

    if (definition.kind === 'date') {
      const selection = activeSheetFilterState.dateSelections[definition.columnKey] ?? {
        buckets: [],
        start: null,
        end: null,
      };
      return (
        <details
          key={definition.columnKey}
          className="rounded-[20px] border border-slate-200 bg-white p-3"
          open
          data-testid={`assistant-spreadsheet-filter-${definition.columnKey}`}
        >
          <summary className="cursor-pointer list-none">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">{definition.header}</p>
                <p className="text-[11px] text-irongrey/70">Month buckets + date range</p>
              </div>
              {selection.buckets.length > 0 || selection.start || selection.end ? (
                <span className="rounded-full bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary">
                  {selection.buckets.length + Number(Boolean(selection.start)) + Number(Boolean(selection.end))}
                </span>
              ) : null}
            </div>
          </summary>
          <div className="mt-3 space-y-3">
            <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto pr-1">
              {definition.options.map((option) => {
                const isSelected = selection.buckets.includes(option.value);
                return (
                  <button
                    key={`${definition.columnKey}:${option.value}`}
                    type="button"
                    className={[
                      'rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-colors',
                      isSelected
                        ? 'border-primary/20 bg-primary text-white'
                        : 'border-slate-200 bg-slate-50 text-irongrey hover:bg-primary/5',
                    ].join(' ')}
                    onClick={() => {
                      updateActiveSheetFilterState((current) => {
                        const currentSelection = current.dateSelections[definition.columnKey] ?? {
                          buckets: [],
                          start: null,
                          end: null,
                        };
                        const nextBuckets = currentSelection.buckets.includes(option.value)
                          ? currentSelection.buckets.filter((value) => value !== option.value)
                          : [...currentSelection.buckets, option.value];
                        return {
                          ...current,
                          dateSelections: {
                            ...current.dateSelections,
                            [definition.columnKey]: {
                              ...currentSelection,
                              buckets: nextBuckets,
                            },
                          },
                        };
                      });
                    }}
                  >
                    {option.label}
                    <span className="ml-1 text-[11px] opacity-80">{option.count}</span>
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[11px] text-irongrey/75">
                Start
                <TailwindDateField
                  min={definition.min}
                  max={definition.max}
                  value={selection.start ?? ''}
                  onChange={(event) => {
                    updateActiveSheetFilterState((current) => {
                      const currentSelection = current.dateSelections[definition.columnKey] ?? {
                        buckets: [],
                        start: null,
                        end: null,
                      };
                      return {
                        ...current,
                        dateSelections: {
                          ...current.dateSelections,
                          [definition.columnKey]: {
                            ...currentSelection,
                            start: event.currentTarget.value || null,
                          },
                        },
                      };
                    });
                  }}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-slate-700"
                />
              </label>
              <label className="text-[11px] text-irongrey/75">
                End
                <TailwindDateField
                  min={definition.min}
                  max={definition.max}
                  value={selection.end ?? ''}
                  onChange={(event) => {
                    updateActiveSheetFilterState((current) => {
                      const currentSelection = current.dateSelections[definition.columnKey] ?? {
                        buckets: [],
                        start: null,
                        end: null,
                      };
                      return {
                        ...current,
                        dateSelections: {
                          ...current.dateSelections,
                          [definition.columnKey]: {
                            ...currentSelection,
                            end: event.currentTarget.value || null,
                          },
                        },
                      };
                    });
                  }}
                  className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-slate-700"
                />
              </label>
            </div>
          </div>
        </details>
      );
    }

    const selection = activeSheetFilterState.numericSelections[definition.columnKey] ?? { min: '', max: '' };
    return (
    <details
      key={definition.columnKey}
      className="rounded-[20px] border border-slate-200 bg-white p-3"
      open
      data-testid={`assistant-spreadsheet-filter-${definition.columnKey}`}
    >
        <summary className="cursor-pointer list-none">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">{definition.header}</p>
              <p className="text-[11px] text-irongrey/70">
                Range {definition.min} to {definition.max}
              </p>
            </div>
          </div>
        </summary>
        <div className="mt-3 space-y-3">
          {definition.presets.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {definition.presets.map((preset) => (
                <button
                  key={`${definition.columnKey}:${preset.id}`}
                  type="button"
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[12px] font-semibold text-irongrey hover:bg-primary/5"
                  onClick={() => {
                    updateActiveSheetFilterState((current) => ({
                      ...current,
                      numericSelections: {
                        ...current.numericSelections,
                        [definition.columnKey]: {
                          min: preset.min == null ? '' : String(preset.min),
                          max: preset.max == null ? '' : String(preset.max),
                        },
                      },
                    }));
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[11px] text-irongrey/75">
              Min
              <input
                type="number"
                step={definition.format === 'percentage' ? '0.01' : '1'}
                value={selection.min}
                onChange={(event) => {
                  updateActiveSheetFilterState((current) => ({
                    ...current,
                    numericSelections: {
                      ...current.numericSelections,
                      [definition.columnKey]: {
                        ...(current.numericSelections[definition.columnKey] ?? { min: '', max: '' }),
                        min: event.currentTarget.value,
                      },
                    },
                  }));
                }}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-slate-700"
              />
            </label>
            <label className="text-[11px] text-irongrey/75">
              Max
              <input
                type="number"
                step={definition.format === 'percentage' ? '0.01' : '1'}
                value={selection.max}
                onChange={(event) => {
                  updateActiveSheetFilterState((current) => ({
                    ...current,
                    numericSelections: {
                      ...current.numericSelections,
                      [definition.columnKey]: {
                        ...(current.numericSelections[definition.columnKey] ?? { min: '', max: '' }),
                        max: event.currentTarget.value,
                      },
                    },
                  }));
                }}
                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-slate-700"
              />
            </label>
          </div>
        </div>
      </details>
    );
  };

  if (presentation === 'portal' && typeof document === 'undefined') {
    return null;
  }

  const activePreset = presets.find((preset) => preset.presetId === selectedPresetId) ?? null;
  const isEmbeddedWorkspace = presentation === 'embedded';
  const workspaceShellClassName = presentation === 'embedded'
    ? 'flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-[#E5E7EB] bg-[#F8FAF9] shadow-[0_0px_3px_2px_rgba(206,242,237,0.28)]'
    : 'flex h-full min-h-0 flex-col bg-[#F8FAF9]';

  const workspaceShell = (
    <>
      <div
        className={workspaceShellClassName}
        data-testid="assistant-spreadsheet-workspace-shell"
      >
        <div className="flex h-full flex-col">
          <header className="border-b border-[#E5E7EB] bg-white px-4 py-3 lg:px-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F4F6F3] text-[#2697A2]">
                  <FileSpreadsheet size={16} />
                </span>
                <div className="min-w-0">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <h2 className="truncate text-[14px] font-semibold leading-5 text-[#404F4D]">
                      {workingCopy?.title ?? artifact?.title ?? resolvedWorkbookArtifact.label}
                    </h2>
                    {activeSheet ? (
                      <span
                        className="shrink-0 rounded-sm border border-[#E5E7EB] bg-[#F8FAF9] px-2 py-0.5 text-[11px] font-medium text-[#6B7280]"
                        data-testid="assistant-spreadsheet-header-row-count"
                      >
                        {activeSheetView.visibleRowCount} rows
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 line-clamp-1 max-w-3xl text-[12px] leading-5 text-[#6B7280]">
                    {activeSheet
                      ? `${activeSheet.name} · ${workingCopy?.sheets.length ?? artifact?.sheets.length ?? 0} sheet${(workingCopy?.sheets.length ?? artifact?.sheets.length ?? 0) === 1 ? '' : 's'}`
                      : (workingCopy?.description ?? artifact?.description ?? 'Review, edit, version, and export the assistant-generated workbook.')}
                  </p>
                  {workingCopy?.description || artifact?.description ? (
                    <p className="mt-0.5 line-clamp-1 max-w-3xl text-[11px] leading-4 text-[#9CA3AF]">
                      {workingCopy?.description ?? artifact?.description}
                    </p>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                className={reportPreviewIconButtonClass}
                onClick={onClose}
                aria-label="Close spreadsheet workspace"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {resolvedDownloadArtifact ? (
                <a
                  href={resolvedDownloadArtifact.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={reportPreviewPrimaryButtonClass}
                >
                  <FileSpreadsheet size={14} />
                  Download .xlsx
                </a>
              ) : null}
              <button
                type="button"
                className={reportPreviewSecondaryButtonClass}
                onClick={() => setExportModalOpen(true)}
                disabled={!onExport}
                data-testid="assistant-spreadsheet-export-trigger"
              >
                {exportState === 'submitting' ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                Export to Google Sheets
              </button>
              {!isEmbeddedWorkspace ? (
                <button
                  type="button"
                  className={reportPreviewSecondaryButtonClass}
                  onClick={handleSaveDraft}
                  disabled={!workingCopy || saveState === 'saving'}
                >
                  {saveState === 'saving' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Save version
                </button>
              ) : null}
              {!isEmbeddedWorkspace ? (
                <div className="inline-flex h-8 items-center rounded-sm border border-[#E5E7EB] bg-[#F8FAF9] px-3 text-[11px] font-medium text-[#6B7280]">
                  Bridge: {bridgeStatusLabel}
                </div>
              ) : null}
              {!isEmbeddedWorkspace ? (
                !bridgeConnected ? (
                  <button
                    type="button"
                    className={reportPreviewSecondaryButtonClass}
                    onClick={() => { void onStartBridgeAuth?.(); }}
                    disabled={!bridgeSupportedEnvironment}
                  >
                    {bridgeSupportedEnvironment ? 'Connect bridge' : 'Download only'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className={reportPreviewSecondaryButtonClass}
                    onClick={() => { void onDisconnectBridge?.(); }}
                  >
                    Disconnect
                  </button>
                )
              ) : null}
            </div>
            {!bridgeSupportedEnvironment ? (
              <p className="mt-3 text-sm text-irongrey/75">
                Live Google Sheets export is unavailable in this environment. Workbook download remains available.
              </p>
            ) : null}
            {saveMessage ? (
              <p className={`mt-3 text-sm ${saveState === 'error' ? 'text-strawberry' : 'text-irongrey/80'}`}>
                {saveMessage}
              </p>
            ) : null}
            {exportMessage ? (
              <p className={`mt-2 text-sm ${exportState === 'error' ? 'text-strawberry' : 'text-irongrey/80'}`}>
                {exportMessage}
              </p>
            ) : null}
          </header>

          <div className="flex min-h-0 flex-1">
            <aside
              className={isEmbeddedWorkspace
                ? 'hidden'
                : 'hidden w-[18rem] shrink-0 min-h-0 overflow-y-auto border-r border-[#E5E7EB] bg-[#F8FAF9] p-3 lg:flex lg:flex-col xl:w-[20rem]'}
              data-testid="assistant-spreadsheet-sidebar"
            >
              <div
                className={`${reportPreviewSurfaceClass} p-3`}
                data-testid="assistant-spreadsheet-filter-rail"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]">Filters</p>
                    <p className="mt-1 text-[12px] text-[#6B7280]">
                      Generated from the current sheet data.
                    </p>
                  </div>
                  {activeSheetView.activeFilterChips.length > 0 ? (
                    <button
                      type="button"
                      className={reportPreviewSecondaryButtonClass}
                      onClick={clearAllGeneratedFilters}
                    >
                      Clear all
                    </button>
                  ) : null}
                </div>

                {generatedFilterDefinitions.length > 0 ? (
                  <div className="mt-4 space-y-3 pr-1">
                    {generatedFilterDefinitions.map((definition) => renderGeneratedFilterSection(definition))}
                  </div>
                ) : (
                  <p className="mt-4 rounded-sm border border-dashed border-[#E5E7EB] bg-[#F8FAF9] px-3 py-3 text-[12px] text-[#6B7280]">
                    This sheet does not expose meaningful generated filters yet.
                  </p>
                )}
              </div>

              <div className="mt-5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]">Versions</p>
                <div className="mt-3 space-y-2">
                  {draftVersions.length > 0 ? draftVersions.map((version) => (
                    <button
                      key={version.versionId}
                      type="button"
                      className={[
                        'w-full rounded-sm border px-3 py-2 text-left text-[12px] transition-colors',
                        version.isCurrent
                          ? 'border-[#2697A2]/25 bg-white text-[#404F4D]'
                          : 'border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-[#F8FAF9]',
                      ].join(' ')}
                      onClick={() => void handleRestoreVersion(version.versionId)}
                    >
                      <p className="font-semibold">v{version.versionNumber}</p>
                      <p className="mt-1 text-[11px] text-irongrey/75">{version.updatedBy} · {version.savedAt || 'Saved'}</p>
                    </button>
                  )) : (
                    <p className="rounded-sm border border-dashed border-[#E5E7EB] bg-white px-3 py-3 text-[12px] text-[#6B7280]">
                      Save a new version to start draft history.
                    </p>
                  )}
                </div>
              </div>

              {selectedCell && activeSheet ? (
                <div className={`${reportPreviewSurfaceClass} mt-5 p-3`}>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]">Selected cell</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{selectedCell.columnKey}</p>
                  {selectedFormulaPreview != null ? (
                    <p className="mt-2 text-xs text-irongrey/75">Formula preview: {String(selectedFormulaPreview)}</p>
                  ) : null}
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <label className="text-[11px] text-irongrey/75">
                      Background
                      <input
                        type="color"
                        value={selectedCellFormat?.backgroundColor ?? '#ffffff'}
                        className="mt-1 h-9 w-full rounded-lg border border-gray-200 bg-white"
                        onChange={(event) => {
                          if (!workingCopy || !selectedCell || !activeSheet) return;
                          commitWorkbook(setSpreadsheetWorkbookCellFormat({
                            workbook: workingCopy,
                            sheetId: activeSheet.id,
                            rowIndex: selectedCell.rowIndex,
                            columnKey: selectedCell.columnKey,
                            format: {
                              ...(selectedCellFormat ?? {}),
                              backgroundColor: event.currentTarget.value,
                            },
                          }) as AdmissionsAssistantWorkbookArtifact);
                        }}
                      />
                    </label>
                    <label className="text-[11px] text-irongrey/75">
                      Text
                      <input
                        type="color"
                        value={selectedCellFormat?.textColor ?? '#1e293b'}
                        className="mt-1 h-9 w-full rounded-lg border border-gray-200 bg-white"
                        onChange={(event) => {
                          if (!workingCopy || !selectedCell || !activeSheet) return;
                          commitWorkbook(setSpreadsheetWorkbookCellFormat({
                            workbook: workingCopy,
                            sheetId: activeSheet.id,
                            rowIndex: selectedCell.rowIndex,
                            columnKey: selectedCell.columnKey,
                            format: {
                              ...(selectedCellFormat ?? {}),
                              textColor: event.currentTarget.value,
                            },
                          }) as AdmissionsAssistantWorkbookArtifact);
                        }}
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    className={`mt-3 w-full rounded-full border px-3 py-2 text-[12px] font-semibold ${
                      selectedCellFormat?.bold ? 'border-primary/25 bg-primary/5 text-primary' : 'border-gray-200 bg-white text-irongrey'
                    }`}
                    onClick={() => {
                      if (!workingCopy || !selectedCell || !activeSheet) return;
                      commitWorkbook(setSpreadsheetWorkbookCellFormat({
                        workbook: workingCopy,
                        sheetId: activeSheet.id,
                        rowIndex: selectedCell.rowIndex,
                        columnKey: selectedCell.columnKey,
                        format: {
                          ...(selectedCellFormat ?? {}),
                          bold: !selectedCellFormat?.bold,
                        },
                      }) as AdmissionsAssistantWorkbookArtifact);
                    }}
                  >
                    Toggle bold
                  </button>
                </div>
              ) : null}
            </aside>

            <div className="min-h-0 flex-1 overflow-auto bg-[#F8FAF9] px-3 py-3 lg:px-5 lg:py-4">
              {isLoading ? (
                <div className="flex h-full items-center justify-center text-sm text-irongrey/70">
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Loading spreadsheet workspace…
                </div>
              ) : errorMessage ? (
                <SupportErrorMessage>
                  {errorMessage}
                </SupportErrorMessage>
              ) : activeSheet && workingCopy ? (
                <div className="space-y-3">
                  {!isEmbeddedWorkspace ? (
                    <div className={`${reportPreviewSurfaceClass} flex flex-wrap items-center gap-2 px-3 py-2`}>
                      <button
                        type="button"
                        className={reportPreviewSecondaryButtonClass}
                        onClick={handleUndo}
                        disabled={historyPast.length === 0}
                      >
                        <Undo2 size={12} />
                        Undo
                      </button>
                      <button
                        type="button"
                        className={reportPreviewSecondaryButtonClass}
                        onClick={handleRedo}
                        disabled={historyFuture.length === 0}
                      >
                        <Redo2 size={12} />
                        Redo
                      </button>
                      <button
                        type="button"
                        className={reportPreviewSecondaryButtonClass}
                        onClick={() => commitWorkbook(addSpreadsheetWorkbookRow({
                          workbook: workingCopy,
                          sheetId: activeSheet.id,
                        }) as AdmissionsAssistantWorkbookArtifact)}
                      >
                        <Plus size={12} />
                        Row
                      </button>
                      <input
                        value={newColumnHeader}
                        onChange={(event) => setNewColumnHeader(event.currentTarget.value)}
                        placeholder="New column"
                        className={reportPreviewInputClass}
                      />
                      <button
                        type="button"
                        className={reportPreviewSecondaryButtonClass}
                        onClick={() => {
                          commitWorkbook(addSpreadsheetWorkbookColumn({
                            workbook: workingCopy,
                            sheetId: activeSheet.id,
                            header: newColumnHeader || 'New Column',
                          }) as AdmissionsAssistantWorkbookArtifact);
                          setNewColumnHeader('');
                        }}
                      >
                        <Plus size={12} />
                        Column
                      </button>
                      <input
                        value={newSheetName}
                        onChange={(event) => setNewSheetName(event.currentTarget.value)}
                        placeholder="New sheet"
                        className={reportPreviewInputClass}
                      />
                      <button
                        type="button"
                        className={reportPreviewSecondaryButtonClass}
                        onClick={() => {
                          const nextWorkbook = addSpreadsheetWorkbookSheet({
                            workbook: workingCopy,
                            name: newSheetName || undefined,
                          }) as AdmissionsAssistantWorkbookArtifact;
                          setActiveSheetId(nextWorkbook.sheets[nextWorkbook.sheets.length - 1]?.id ?? activeSheet.id);
                          commitWorkbook(nextWorkbook);
                          setNewSheetName('');
                        }}
                      >
                        <Plus size={12} />
                        Sheet
                      </button>
                    </div>
                  ) : null}

                  {workingCopy.sheets.length > 1 ? (
                    <div className={`${reportPreviewSurfaceClass} flex gap-2 overflow-x-auto px-3 py-2`}>
                      {workingCopy.sheets.map((sheet, sheetIndex) => {
                        const isActive = sheet.id === activeSheet.id;
                        return (
                          <div key={sheet.id} className="flex items-center gap-1">
                            <button
                              type="button"
                              className={[
                                'h-8 rounded-sm px-3 text-[12px] font-medium transition-colors',
                                isActive
                                  ? 'bg-[#2697A2] text-white'
                                  : 'border border-[#E5E7EB] bg-white text-[#6B7280] hover:bg-[#F8FAF9]',
                              ].join(' ')}
                              onClick={() => setActiveSheetId(sheet.id)}
                            >
                              {sheet.name}
                            </button>
                            {!isEmbeddedWorkspace ? (
                              <>
                                <button
                                  type="button"
                                  className={reportPreviewIconButtonClass}
                                  onClick={() => commitWorkbook(reorderSpreadsheetWorkbookSheets({
                                    workbook: workingCopy,
                                    fromIndex: sheetIndex,
                                    toIndex: Math.max(0, sheetIndex - 1),
                                  }) as AdmissionsAssistantWorkbookArtifact)}
                                >
                                  <RotateCcw size={12} />
                                </button>
                                <button
                                  type="button"
                                  className={reportPreviewIconButtonClass}
                                  onClick={() => commitWorkbook(reorderSpreadsheetWorkbookSheets({
                                    workbook: workingCopy,
                                    fromIndex: sheetIndex,
                                    toIndex: Math.min(workingCopy.sheets.length - 1, sheetIndex + 1),
                                  }) as AdmissionsAssistantWorkbookArtifact)}
                                >
                                  <Redo2 size={12} />
                                </button>
                              </>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}

                  <div className={isEmbeddedWorkspace ? 'hidden' : `${reportPreviewSurfaceClass} p-3`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]">Sheet settings</span>
                          <button
                            type="button"
                            className={reportPreviewSecondaryButtonClass}
                            onClick={() => commitWorkbook(deleteSpreadsheetWorkbookSheet({
                              workbook: workingCopy,
                              sheetId: activeSheet.id,
                            }) as AdmissionsAssistantWorkbookArtifact)}
                          >
                            <Trash2 size={12} className="inline-block align-text-bottom" /> Delete sheet
                          </button>
                        </div>
                        <InlineCommitInput
                          value={activeSheet.name}
                          onCommit={(value) => commitWorkbook(updateSpreadsheetWorkbookSheet({
                            workbook: workingCopy,
                            sheetId: activeSheet.id,
                            name: String(value || activeSheet.name),
                          }) as AdmissionsAssistantWorkbookArtifact)}
                        />
                      </div>
                      <div
                        className="min-w-[14rem] rounded-sm border border-[#E5E7EB] bg-[#F8FAF9] px-4 py-3 text-right"
                        data-testid={isEmbeddedWorkspace ? undefined : 'assistant-spreadsheet-visible-row-count'}
                      >
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]">Visible rows</p>
                        <p className="mt-1 text-sm font-semibold text-[#404F4D]">
                          {activeSheetView.visibleRowCount} of {activeSheetView.totalRowCount}
                        </p>
                        <p className="mt-1 text-[12px] text-[#6B7280]">
                          Filters update this view instantly without changing the saved workbook.
                        </p>
                      </div>
                    </div>
                    <div
                      className="mt-4 rounded-sm border border-dashed border-[#E5E7EB] bg-[#F8FAF9] px-4 py-3"
                      data-testid={isEmbeddedWorkspace ? undefined : 'assistant-spreadsheet-active-filters'}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]">Active view filters</p>
                        {activeSheetView.activeFilterChips.length > 0 ? (
                          <button
                            type="button"
                            className={reportPreviewSecondaryButtonClass}
                            onClick={clearAllGeneratedFilters}
                          >
                            Clear all
                          </button>
                        ) : null}
                      </div>
                      {activeSheetView.activeFilterChips.length > 0 ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {activeSheetView.activeFilterChips.map((chip) => (
                            <button
                              key={chip.id}
                              type="button"
                              className="inline-flex h-8 items-center gap-2 rounded-sm border border-[#E5E7EB] bg-white px-3 text-[12px] font-medium text-[#404F4D] hover:bg-[#F8FAF9]"
                              onClick={() => clearActiveFilterChip(chip)}
                            >
                              <span className="text-[#2697A2]">{chip.label}</span>
                              <span className="text-[#6B7280]">{chip.valueLabel}</span>
                              <X size={12} className="text-[#9CA3AF]" />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-[#6B7280]">
                          Use the generated filter rail to the left for quick chips, searchable multi-selects, and data-driven ranges.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className={`${reportPreviewSurfaceClass} overflow-hidden`}>
                    <div
                      className="flex min-h-10 flex-wrap items-center justify-between gap-2 border-b border-[#E5E7EB] bg-white px-3 py-2"
                      data-testid={isEmbeddedWorkspace ? 'assistant-spreadsheet-active-filters' : undefined}
                    >
                      <div
                        className="min-w-0"
                        data-testid={isEmbeddedWorkspace ? 'assistant-spreadsheet-visible-row-count' : undefined}
                      >
                        <p className="truncate text-[12px] font-semibold leading-4 text-[#404F4D]">
                          {activeSheet.name}
                        </p>
                        <p className="mt-0.5 text-[11px] leading-4 text-[#9CA3AF]">
                          Showing {activeSheetView.visibleRowCount} of {activeSheetView.totalRowCount} rows · {activeSheet.columns.length} columns
                        </p>
                      </div>
                      {activeSheetView.activeFilterChips.length > 0 ? (
                        <button
                          type="button"
                          className={reportPreviewSecondaryButtonClass}
                          onClick={clearAllGeneratedFilters}
                        >
                          Clear filters
                        </button>
                      ) : null}
                    </div>
                    <div className="overflow-auto">
                    <table className="min-w-full border-collapse text-sm">
                      <thead className="sticky top-0 z-10 bg-[#F4F6F3]">
                        <tr>
                          <th className="w-[7rem] border-b border-[#E5E7EB] px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[#6B7280]">
                            Row
                          </th>
                          {activeSheet.columns.map((column, columnIndex) => (
                            <th
                              key={column.key}
                              className={`border-b border-[#E5E7EB] px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[#6B7280] ${sheetColumnWidthClass(column.width)}`}
                            >
                              <div className="space-y-1.5">
                                <InlineCommitInput
                                  value={column.header}
                                  onCommit={(value) => commitWorkbook(updateSpreadsheetWorkbookColumn({
                                    workbook: workingCopy,
                                    sheetId: activeSheet.id,
                                    columnKey: column.key,
                                    header: String(value || column.header),
                                  }) as AdmissionsAssistantWorkbookArtifact)}
                                />
                                <div className="flex flex-wrap items-center gap-1">
                                  <button
                                    type="button"
                                    className={reportPreviewIconButtonClass}
                                    onClick={() => commitWorkbook(reorderSpreadsheetWorkbookColumns({
                                      workbook: workingCopy,
                                      sheetId: activeSheet.id,
                                      fromIndex: columnIndex,
                                      toIndex: Math.max(0, columnIndex - 1),
                                    }) as AdmissionsAssistantWorkbookArtifact)}
                                  >
                                    <RotateCcw size={12} />
                                  </button>
                                  <button
                                    type="button"
                                    className={reportPreviewIconButtonClass}
                                    onClick={() => commitWorkbook(reorderSpreadsheetWorkbookColumns({
                                      workbook: workingCopy,
                                      sheetId: activeSheet.id,
                                      fromIndex: columnIndex,
                                      toIndex: Math.min(activeSheet.columns.length - 1, columnIndex + 1),
                                    }) as AdmissionsAssistantWorkbookArtifact)}
                                  >
                                    <Redo2 size={12} />
                                  </button>
                                  <button
                                    type="button"
                                    className={reportPreviewIconButtonClass}
                                    onClick={() => commitWorkbook(deleteSpreadsheetWorkbookColumn({
                                      workbook: workingCopy,
                                      sheetId: activeSheet.id,
                                      columnKey: column.key,
                                    }) as AdmissionsAssistantWorkbookArtifact)}
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {activeSheetView.rows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={activeSheet.columns.length + 1}
                              className="px-4 py-8 text-center text-sm text-irongrey/75"
                            >
                              No rows match the active filters on this sheet.
                            </td>
                          </tr>
                        ) : activeSheetView.rows.map(({ row, sourceRowIndex }, visibleRowIndex) => (
                          <tr
                            key={`row-${sourceRowIndex}`}
                            className={`${visibleRowIndex % 2 === 0 ? 'bg-white' : 'bg-[#F8FAF9]'} transition-colors hover:bg-[rgba(38,151,162,0.03)]`}
                          >
                            <td className="border-b border-[#E5E7EB]/70 px-3 py-2.5 align-top">
                              <div className="flex items-center gap-1">
                                <span className="text-[11px] font-semibold text-[#6B7280]">#{sourceRowIndex + 1}</span>
                                <button
                                  type="button"
                                  className={reportPreviewIconButtonClass}
                                  onClick={() => commitWorkbook(duplicateSpreadsheetWorkbookRow({
                                    workbook: workingCopy,
                                    sheetId: activeSheet.id,
                                    rowIndex: sourceRowIndex,
                                  }) as AdmissionsAssistantWorkbookArtifact)}
                                >
                                  <Copy size={11} />
                                </button>
                                <button
                                  type="button"
                                  className={reportPreviewIconButtonClass}
                                  onClick={() => commitWorkbook(deleteSpreadsheetWorkbookRow({
                                    workbook: workingCopy,
                                    sheetId: activeSheet.id,
                                    rowIndex: sourceRowIndex,
                                  }) as AdmissionsAssistantWorkbookArtifact)}
                                >
                                  <Trash2 size={11} />
                                </button>
                              </div>
                            </td>
                            {activeSheet.columns.map((column) => {
                              const cellFormat = getSpreadsheetWorkbookCellFormat({
                                sheet: activeSheet,
                                rowIndex: sourceRowIndex,
                                columnKey: column.key,
                              });
                              return (
                                <td
                                  key={`${sourceRowIndex}-${column.key}`}
                                  className="border-b border-[#E5E7EB]/70 px-2 py-2 align-top text-[13px] leading-6 text-[#404F4D]"
                                  style={{
                                    backgroundColor: cellFormat?.backgroundColor,
                                    color: cellFormat?.textColor,
                                    fontWeight: cellFormat?.bold ? 700 : undefined,
                                  }}
                                >
                                  <InlineCommitInput
                                    value={row[column.key] ?? null}
                                    onFocus={() => setSelectedCell({ rowIndex: sourceRowIndex, columnKey: column.key })}
                                    onCommit={(value) => commitWorkbook(updateSpreadsheetWorkbookCell({
                                      workbook: workingCopy,
                                      sheetId: activeSheet.id,
                                      rowIndex: sourceRowIndex,
                                      columnKey: column.key,
                                      value,
                                    }) as AdmissionsAssistantWorkbookArtifact)}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                    <div className="border-t border-[#E5E7EB] bg-[#F8FAF9] px-3 py-2">
                      <button
                        type="button"
                        className={reportPreviewSecondaryButtonClass}
                        onClick={() => commitWorkbook(addSpreadsheetWorkbookRow({
                          workbook: workingCopy,
                          sheetId: activeSheet.id,
                        }) as AdmissionsAssistantWorkbookArtifact)}
                      >
                        <Plus size={12} />
                        Add row
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-gray-200 bg-white p-4 text-sm text-irongrey/70">
                  No workbook sheets are available.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AppDialogFrame
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        title="Choose export target"
        description="Create a new sheet or update an existing one. Presets are optional."
        testId="assistant-spreadsheet-export-dialog"
        dismissOnBackdrop={false}
        insetMode="viewport"
        layerZIndex={1420}
        maxWidthClassName="max-w-none"
        panelClassName="w-[min(100%,42rem)] rounded-[28px] border-white/70 bg-white shadow-[0_30px_120px_rgba(18,38,41,0.28)]"
        bodyPaddingClassName="p-6"
      >
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={`rounded-full border px-3 py-2 text-[12px] font-semibold ${destinationMode === 'new_sheet' ? 'border-primary/20 bg-primary/5 text-primary' : 'border-gray-200 bg-white text-irongrey'}`}
              onClick={() => setDestinationMode('new_sheet')}
            >
              <Link2 size={13} className="mr-1 inline-block" />
              Create new sheet
            </button>
            <button
              type="button"
              className={`rounded-full border px-3 py-2 text-[12px] font-semibold ${destinationMode === 'existing_sheet' ? 'border-primary/20 bg-primary/5 text-primary' : 'border-gray-200 bg-white text-irongrey'}`}
              onClick={() => setDestinationMode('existing_sheet')}
            >
              <ExternalLink size={13} className="mr-1 inline-block" />
              Update existing
            </button>
          </div>

          <label className="block text-sm font-semibold text-slate-900">
            File name
            <input
              value={draftFileName}
              onChange={(event) => setDraftFileName(event.currentTarget.value)}
              className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm"
            />
          </label>

          {presets.length > 0 ? (
            <label className="block text-sm font-semibold text-slate-900">
              Preset
              <select
                value={selectedPresetId}
                onChange={(event) => {
                  const nextPresetId = event.currentTarget.value;
                  setSelectedPresetId(nextPresetId);
                  const preset = presets.find((entry) => entry.presetId === nextPresetId) ?? null;
                  if (!preset) {
                    return;
                  }
                  setDestinationMode(preset.destinationType);
                  setDestinationFileInput(preset.spreadsheetUrl ?? preset.destinationFileId ?? '');
                  setDestinationFolderInput(preset.destinationFolderId ?? '');
                  if (preset.fileNameTemplate) {
                    setDraftFileName(preset.fileNameTemplate);
                  }
                }}
                className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm"
              >
                <option value="">No preset</option>
                {presets.map((preset) => (
                  <option key={preset.presetId} value={preset.presetId}>{preset.label}</option>
                ))}
              </select>
            </label>
          ) : null}

          {destinationMode === 'existing_sheet' ? (
            <label className="block text-sm font-semibold text-slate-900">
              Google Sheet URL or file id
              <input
                value={destinationFileInput}
                onChange={(event) => setDestinationFileInput(event.currentTarget.value)}
                placeholder="Paste a Google Sheet URL or file id"
                className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm"
              />
            </label>
          ) : (
            <label className="block text-sm font-semibold text-slate-900">
              Drive folder id (optional)
              <input
                value={destinationFolderInput}
                onChange={(event) => setDestinationFolderInput(event.currentTarget.value)}
                placeholder="Folder id for new sheet placement"
                className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm"
              />
            </label>
          )}

          <div className="rounded-[22px] border border-gray-200 bg-slate-50 px-4 py-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary/70">Preset management</p>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <input
                value={presetLabel}
                onChange={(event) => setPresetLabel(event.currentTarget.value)}
                placeholder="Preset label"
                className="flex-1 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm"
              />
              <button
                type="button"
                className="rounded-full border border-gray-200 bg-white px-4 py-3 text-[12px] font-semibold text-irongrey"
                onClick={() => {
                  if (!presetLabel || !onSavePreset) {
                    return;
                  }
                  void onSavePreset({
                    presetId: activePreset?.presetId,
                    label: presetLabel,
                    destinationType: destinationMode,
                    destinationFileId: destinationMode === 'existing_sheet' && destinationFileInput
                      ? extractSpreadsheetId(destinationFileInput)
                      : undefined,
                    destinationFolderId: destinationMode === 'new_sheet' ? (destinationFolderInput || undefined) : undefined,
                    spreadsheetUrl: destinationMode === 'existing_sheet' ? destinationFileInput || null : null,
                    fileNameTemplate: draftFileName || null,
                  });
                  setPresetLabel('');
                }}
              >
                Save preset
              </button>
              {activePreset && onDeletePreset ? (
                <button
                  type="button"
                  className="rounded-full border border-gray-200 bg-white px-4 py-3 text-[12px] font-semibold text-irongrey"
                  onClick={() => void onDeletePreset({ presetId: activePreset.presetId })}
                >
                  <Link2Off size={12} className="mr-1 inline-block" />
                  Delete preset
                </button>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-irongrey/70">
              {bridgeConnected
                ? 'Connected to Google Sheets.'
                : bridgeSupportedEnvironment
                  ? `Connect the bridge first to export. Current state: ${bridgeState.replace(/_/g, ' ')}.`
                  : 'This environment supports download only.'}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {!bridgeConnected && bridgeSupportedEnvironment ? (
                <button
                  type="button"
                  className="rounded-full border border-gray-200 bg-white px-4 py-2 text-[12px] font-semibold text-irongrey"
                  onClick={() => { void onStartBridgeAuth?.(); }}
                >
                  Connect bridge
                </button>
              ) : null}
              <button
                type="button"
                className="rounded-full border border-gray-200 bg-white px-4 py-2 text-[12px] font-semibold text-irongrey"
                onClick={() => setExportModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-full bg-primary px-4 py-2 text-[12px] font-semibold text-white disabled:opacity-50"
                disabled={!bridgeConnected || exportState === 'submitting' || !onExport}
                onClick={() => {
                  void onExport?.({
                    fileName: draftFileName.replace(/\.json$/i, '.xlsx'),
                    destinationFileId: destinationMode === 'existing_sheet' && destinationFileInput
                      ? extractSpreadsheetId(destinationFileInput)
                      : undefined,
                    destinationFolderId: destinationMode === 'new_sheet'
                      ? (destinationFolderInput || undefined)
                      : undefined,
                  });
                  setExportModalOpen(false);
                }}
              >
                {exportState === 'submitting' ? <Loader2 size={14} className="animate-spin" /> : 'Export now'}
              </button>
            </div>
          </div>
        </div>
      </AppDialogFrame>
    </>
  );

  if (presentation === 'embedded') {
    return (
      <div className="flex h-full min-h-0 flex-col" data-testid="assistant-spreadsheet-workspace">
        {workspaceShell}
      </div>
    );
  }

  return (
    <AppDialogFrame
      open
      onClose={onClose}
      title={workingCopy?.title ?? artifact?.title ?? resolvedWorkbookArtifact.label}
      description="Spreadsheet workspace"
      testId="assistant-spreadsheet-workspace-dialog"
      dismissOnBackdrop={false}
      headerMode="none"
      insetMode="shell-content"
      backdropInsetMode="shell-content"
      maxWidthClassName="max-w-none"
      panelClassName="h-[calc(100dvh-2rem)] max-h-[calc(100dvh-2rem)] rounded-[30px] border-white/70 bg-white shadow-[0_30px_120px_rgba(18,38,41,0.28)]"
      bodyPaddingClassName="p-0"
      bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
    >
      <div className="flex h-full min-h-0 flex-col" data-testid="assistant-spreadsheet-workspace">
        {workspaceShell}
      </div>
    </AppDialogFrame>
  );
};
